import React, { useCallback, useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { createRoot } from 'react-dom/client';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import helpers from 'dom-helpers';
import ChildrenContext from './ChildrenContext';
import eventEmitter from '../utils/EventEmitter';

function DropdownInner(props, ref) {
    const { open: openProp, isCloseDocument, overlay, onToggle, header, children } = props;
    const [open, setOpen] = useState(!!openProp);
    const elementRef = useRef(null);
    const wrapperRef = useRef(null);
    const wrapperRootRef = useRef(null);

    const getClass = useCallback((key, cl) => classNames({
        ...props[key],
        ...cl
    }), [props]);

    const setPositionWrapper = useCallback(() => {
        if (!elementRef.current || !wrapperRef.current) return;
        const elemBox = helpers.offset(elementRef.current);
        helpers.style(wrapperRef.current, {
            position: 'absolute',
            top: (elemBox.top + elemBox.height / 2) + 'px',
            left: elemBox.left + 'px',
            zIndex: 10,
        });
    }, []);

    const clickDocument = useCallback((event) => {
        if (!open) return;
        if (elementRef.current && elementRef.current.contains(event.target)) return;
        if (wrapperRef.current && wrapperRef.current.contains(event.target)) return;
        
        if (event.target.closest('.top-header__menu-toggle')) {
            setOpen(false);
            if (overlay === 'body' && wrapperRef.current) {
                removeChildren();
            }
            setTimeout(() => {
                eventEmitter.emit('toggleMenu');
            }, 50);
            return;
        }
        
        if (event.target.closest('.top-header__user')) {
            handleToggle(event);
            return;
        }
        
        handleToggle(event);
    }, [open, handleToggle]);

    const initChildren = useCallback(() => {
        if (overlay !== 'body') return;
        const wrapper = document.createElement('div');
        wrapperRef.current = wrapper;
        const body = helpers.querySelectorAll(document, 'body')[0];
        body.appendChild(wrapper);
        wrapperRootRef.current = createRoot(wrapper);
        renderChildren();
    }, [overlay]);

    const removeChildren = useCallback(() => {
        const wrapper = wrapperRef.current;
        const root = wrapperRootRef.current;
        wrapperRef.current = null;
        wrapperRootRef.current = null;
        Promise.resolve().then(() => {
            if (root) root.unmount();
            if (wrapper && wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
        });
    }, []);

    const renderChildren = useCallback(() => {
        if (!wrapperRef.current) return;
        setPositionWrapper();
        wrapperRef.current.className = classNames({ 'dropdown': true, 'open': open });
        if (wrapperRootRef.current) {
            if (open) {
                wrapperRootRef.current.render(
                    <ChildrenContext>
                        {children}
                    </ChildrenContext>
                );
            } else {
                wrapperRootRef.current.render(null);
            }
        }
    }, [children, open, setPositionWrapper]);

    const handleToggle = useCallback((event) => {
        if (event && null === event.target.closest('.bm-menu')) {
            let activeHamburger = document.querySelector('.top-header__menu-toggle.active');
            if (null !== activeHamburger) {
                document.querySelector('.top-header__menu-toggle.active').click();
            }
        }
        if (onToggle) {
            onToggle();
            return;
        }
        setOpen(prev => !prev);
    }, [onToggle]);

    useImperativeHandle(ref, () => ({
        onToggle: () => handleToggle(),
    }), [handleToggle]);

    useEffect(() => {
        setOpen(!!openProp);
    }, [openProp]);

    useEffect(() => {
        initChildren();
        if (isCloseDocument) document.body.addEventListener('click', clickDocument);
        if (wrapperRef.current) window.addEventListener('resize', setPositionWrapper);
        return () => {
            if (isCloseDocument) document.body.removeEventListener('click', clickDocument);
            if (wrapperRef.current) window.removeEventListener('resize', setPositionWrapper);
            removeChildren();
        };
    }, [initChildren, isCloseDocument, clickDocument, setPositionWrapper, removeChildren]);

    useEffect(() => {
        renderChildren();
    }, [renderChildren]);

    return (
        <div
            className={getClass('mainClass', { 'dropdown': true, 'open': open })}
            ref={elementRef}
        >
            <button
                className={getClass('headerClass', { 'dropdown-toggle': true, 'btn-empty': true, 'bold': true })}
                onClick={handleToggle}
            >
                {header}
            </button>
            {(!wrapperRef.current && open) ? children : null}
        </div>
    );
}

const Dropdown = forwardRef(DropdownInner);

Dropdown.propTypes = {
    mainClass: PropTypes.object,
    headerClass: PropTypes.object,
    open: PropTypes.bool,
    onToggle: PropTypes.func,
    isCloseDocument: PropTypes.bool,
    overlay: PropTypes.string,
    header: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.element
    ])
};

Dropdown.defaultProps = {
    dropdown: false,
    isCloseDocument: true,
    header: '',
    overlay: '',
    headerClass: {},
    mainClass: {}
};

export default Dropdown;
